import { State, Toast } from '@seiue/ui'
import { useOnMount } from '@seiue/util'
import { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { RichTextRenderer } from 'packages/components/RichText'
import { Activity, LoginTypeEnum, activityApi } from 'packages/sdks-next/growp'

import {
  activityReflectionStorage,
  store,
  userThemeColorAtom,
  activityIdStorage,
  activitySchoolIdStorage,
} from '@/store'
import { useValidUrlParams } from '@/utils/url'

import { Header } from './Header'
import { ActivityLoginContext } from './context'

export interface LoginProps {
  previewData?: {
    logo: string
    bgImage: string
    description: string
    name: string
    themeColor: string
    hide: () => void
  }
}

/**
 * 登录页
 *
 * @returns 登录页
 */
export const ActivityLogin: FC = () => {
  const userPreviewData = localStorage.getItem('user-preview')
  const previewData =
    userPreviewData && userPreviewData !== 'undefined'
      ? JSON.parse(userPreviewData)
      : undefined
  const isPreview = !!previewData && location.pathname !== '/login'
  const [activity, setActivity] = useState<Activity>()
  const [loading, setLoading] = useState<boolean>(true)

  const urlMsg = useValidUrlParams(isPreview)

  const { activityId, schoolId } = urlMsg || {}

  useEffect(() => {
    if (activityId && !isPreview) {
      store.set(activitySchoolIdStorage, schoolId)
      store.set(activityIdStorage, activityId)

      activityApi
        .loadActivityInfo(activityId, {}, { omitAuthorizationHeader: true })
        .then((res) => {
          const { data } = res
          setActivity(data)
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
  }, [activityId, isPreview])

  const activityData = useMemo(
    () => ({
      logo: isPreview ? previewData.logo : activity?.logo || '',
      title: isPreview ? previewData.name : activity?.name || '',
      bgImage: isPreview ? previewData.bgImage : activity?.bgImage || '',
      description: isPreview
        ? previewData.description
        : activity?.description || '',
      themeColor: isPreview
        ? previewData.themeColor
        : activity?.themeColor || '',
    }),
    [isPreview, previewData, activity]
  )

  useEffect(() => {
    if (!isPreview) {
      store.set(userThemeColorAtom, activityData.themeColor)
    }
    document.title = activityData.title || ''
  }, [activityData])

  useOnMount(() => {
    if (!isPreview) {
      store.set(activityReflectionStorage, undefined)
    }
  })

  useEffect(() => {
    // 禁止登录直接到404
    if (!isPreview && activity?.loginType === LoginTypeEnum.Forbid) {
      Toast.error('当前活动禁止访问')
      location.href = '/404'
    }
  }, [activity])

  if (loading || (!isPreview && !activity?.themeColor)) return <State.Loading />

  if (activity?.loginType === LoginTypeEnum.Forbid) return null

  return (
    <ActivityLoginContext.Provider value={{ schoolId, activityId }}>
      <Wrapper>
        <Header
          name={activityData.title}
          logo={activityData.logo}
          isPreview={isPreview}
          themeColor={activityData.themeColor}
          hasInviteCode={activity?.hasInviteCode}
        />
        <Content>
          {activityData.bgImage ? <Banner src={activityData.bgImage} /> : null}
          <Inner>
            <Title>{activityData.title}</Title>
            <RichTextRenderer
              largeFontSize={true}
              value={activityData.description}
            />
          </Inner>
        </Content>
      </Wrapper>
    </ActivityLoginContext.Provider>
  )
}
const Wrapper = styled.div`
  background-color: #fff;
  min-height: 100vh;
  min-width: 720px;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: calc(100vh - 72px);
  overflow: auto;
`
const Banner = styled.img`
  margin: 0 auto;
  width: 100%;
  height: 240px;
  object-fit: cover;
  object-position: center;
`

const Title = styled.h1`
  font-size: 40px;
  font-weight: 500;
  line-height: 56px;
  margin-top: 48px;
  margin-bottom: 16px;
  text-align: center;
  color: ${(props) => props.theme.text._1};
`

const Inner = styled.div`
  box-sizing: content-box;
  width: 720px;
  padding: 0 20px 20px 20px;
  margin: 0 auto;
`
